// src/features/account/VerifyPage.js
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import DWLFLoader from '../../components/DWLFLoader';
import apiClient from '../../api/apiClient';

const VerifyPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setErrorMessage('Invalid verification link. No token provided.');
        return;
      }

      try {
        await apiClient.post('/auth/verify', { token });
        setStatus('success');
      } catch (error) {
        setStatus('error');
        const message = error.response?.data?.error || 'Verification failed. Please try again.';
        setErrorMessage(message);
      }
    };

    verifyEmail();
  }, [token]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'var(--dwlf-bg)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
        position: 'relative',
      }}
    >
      {/* Subtle noise texture */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.025,
          pointerEvents: 'none',
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          zIndex: 0,
        }}
      />

      {/* Logo */}
      <Typography
        component={Link}
        to="/"
        sx={{
          fontFamily: '"Space Grotesk", sans-serif',
          fontSize: '2rem',
          fontWeight: 600,
          color: 'var(--dwlf-accent)',
          textDecoration: 'none',
          mb: 6,
          letterSpacing: '-0.02em',
          position: 'relative',
          zIndex: 1,
        }}
      >
        DWLF
      </Typography>

      <Box
        sx={{
          width: '100%',
          maxWidth: 420,
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
        }}
      >
        {/* Verifying State */}
        {status === 'verifying' && (
          <>
            <Box sx={{ mb: 4 }}>
              <DWLFLoader size="medium" />
            </Box>
            <Typography
              sx={{
                fontFamily: '"Space Grotesk", sans-serif',
                fontSize: '1.5rem',
                fontWeight: 500,
                color: 'var(--dwlf-text)',
                mb: 2,
                letterSpacing: '-0.02em',
              }}
            >
              Verifying your email...
            </Typography>
            <Typography
              sx={{
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: '0.85rem',
                color: 'var(--dwlf-ink-50)',
              }}
            >
              Please wait a moment
            </Typography>
          </>
        )}

        {/* Success State */}
        {status === 'success' && (
          <>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: 'var(--dwlf-up-10)',
                border: '2px solid var(--dwlf-up)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
              }}
            >
              <Typography sx={{ fontSize: '2rem' }}>✓</Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: '"Space Grotesk", sans-serif',
                fontSize: '1.75rem',
                fontWeight: 500,
                color: 'var(--dwlf-text)',
                mb: 2,
                letterSpacing: '-0.02em',
              }}
            >
              Email verified!
            </Typography>
            <Typography
              sx={{
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: '0.9rem',
                color: 'var(--dwlf-ink-60)',
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              Your account has been verified successfully.<br />
              You can now sign in to your account.
            </Typography>
            <Button
              component={Link}
              to="/login"
              sx={{
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.8rem',
                fontWeight: 500,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--dwlf-bg)',
                backgroundColor: 'var(--dwlf-accent)',
                borderRadius: 0,
                py: 1.5,
                px: 6,
                textDecoration: 'none',
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: 'var(--dwlf-text)',
                },
              }}
            >
              Sign In
            </Button>
          </>
        )}

        {/* Error State */}
        {status === 'error' && (
          <>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: 'var(--dwlf-down-10)',
                border: '2px solid var(--dwlf-down)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
              }}
            >
              <Typography sx={{ fontSize: '2rem' }}>✕</Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: '"Space Grotesk", sans-serif',
                fontSize: '1.75rem',
                fontWeight: 500,
                color: 'var(--dwlf-text)',
                mb: 2,
                letterSpacing: '-0.02em',
              }}
            >
              Verification failed
            </Typography>
            <Typography
              sx={{
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: '0.9rem',
                color: 'var(--dwlf-ink-60)',
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              {errorMessage}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                component={Link}
                to="/register"
                sx={{
                  fontFamily: '"Space Mono", monospace',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'var(--dwlf-accent)',
                  backgroundColor: 'transparent',
                  border: '1px solid var(--dwlf-accent-30)',
                  borderRadius: 0,
                  py: 1.5,
                  px: 4,
                  textDecoration: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'var(--dwlf-accent-10)',
                    borderColor: 'var(--dwlf-accent)',
                  },
                }}
              >
                Try Again
              </Button>
              <Button
                component={Link}
                to="/login"
                sx={{
                  fontFamily: '"Space Mono", monospace',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'var(--dwlf-bg)',
                  backgroundColor: 'var(--dwlf-accent)',
                  borderRadius: 0,
                  py: 1.5,
                  px: 4,
                  textDecoration: 'none',
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'var(--dwlf-text)',
                  },
                }}
              >
                Sign In
              </Button>
            </Box>
          </>
        )}
      </Box>

      {/* Footer */}
      <Typography
        sx={{
          position: 'absolute',
          bottom: 32,
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: '0.7rem',
          color: 'var(--dwlf-ink-30)',
          letterSpacing: '0.1em',
        }}
      >
        SIGNAL FROM NOISE
      </Typography>
    </Box>
  );
};

export default VerifyPage;
